import { useState } from 'react';
import { Accordion, Panel } from 'baseui/accordion';
import { Plus, CheckIndeterminate } from 'baseui/icon';
import { cx } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import styles from './migrationpanel.module.css';
import { useClientRenderDelay } from 'utils/hooks/useClientRenderDelay';

interface IASIAccordion {
  title: string;
  lists: string[];
  disc?: boolean;
  expanded: boolean;
  linkText?: string;
  link?: string;
}

export const ASIAccordion = ({
  title,
  lists,
  disc = true,
  expanded,
  linkText,
  link,
}: IASIAccordion) => {
  const [isExpanded, setExpanded] = useState(expanded);

  return (
    <div className='min-w-screen-sm mb-4 min-h-12 w-full max-w-full text-white'>
      <Accordion onChange={() => setExpanded((prevState) => !prevState)}>
        <Panel
          expanded={isExpanded}
          onChange={(v) => {
            setExpanded(v.expanded);
          }}
          overrides={{
            ToggleIcon: {
              component: ({ $expanded }: { $expanded: boolean }) =>
                $expanded ? (
                  <CheckIndeterminate size={20} />
                ) : (
                  <Plus size={20} />
                ),
            },
            Header: {
              style: {
                fontWeight: '700',
                fontSize: '16px',
                fontFamily: 'var(--font-haben-grotesk)',
                lineHeight: '20px',
                paddingTop: '0px',
                paddingBottom: 0,
                paddingLeft: '0px',
                paddingRight: '0px',
                marginLeft: 0,
                marginRight: 0,
                marginTop: 0,
                marginBottom: 0,
                color: '#F7F8F8',
                ':hover': {
                  color: '#F7F8F8',
                },
              },
            },
            Content: {
              style: {
                backgroundColor: 'transparent',
                paddingLeft: '0px',
                paddingRight: '0px',
                paddingTop: '4px',
                paddingBottom: '12px',
                marginLeft: 0,
                marginRight: 0,
                marginTop: 0,
                marginBottom: 0,
              },
            },
            PanelContainer: {
              style: {
                borderLeftWidth: '0',
                borderRightWidth: '0',
                borderTopWidth: '0',
                borderBottomWidth: '0',
                borderLeftStyle: 'none',
                borderBottomStyle: 'none',
                borderRightStyle: 'none',
                borderTopStyle: 'none',
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                marginLeft: 0,
                marginRight: 0,
                marginTop: 0,
                marginBottom: 0,
              },
            },
          }}
          title={title}
        >
          <div className={cx(twMerge(styles.migrateFont), 'mt-2 pe-2 text-xs')}>
            <ul className={disc ? 'list-disc' : 'list-none'}>
              {lists?.map((list, index) => {
                return (
                  <li
                    className='font-planar text-sm text-[#C5C6C5]'
                    key={index}
                    dangerouslySetInnerHTML={{ __html: list }}
                  />
                );
              })}
            </ul>
            {linkText && (
              <div className='mt-2 flex items-end justify-end underline'>
                <a href={link} target='blank'>
                  {linkText}
                </a>
              </div>
            )}
          </div>
        </Panel>
      </Accordion>
    </div>
  );
};

export const ASITokenMigrationDisclaimer = () => {
  const { t } = useTranslation('migrate');
  const csr = useClientRenderDelay();
  const conversionRates = t('CONVERSION_RATE_GUIDE_LIST', {
    returnObjects: true,
  }) as string[];
  const furtherInformation = t('FURTHER_INFORMATION_GUIDE_LIST', {
    returnObjects: true,
  }) as string[];

  return (
    <div className={cx(twMerge(styles.migrateBubbles), 'ml-0')}>
      <h2
        className={cx(
          twMerge(
            styles.migrateFont,
            'mb-9 mt-8 text-base font-normal text-white md:mt-0'
          )
        )}
      >
        {t('ASI_TOKEN_MIGRATION')}
      </h2>
      {csr && (
        <>
          <ASIAccordion
            expanded={true}
            lists={conversionRates}
            title={t('CONVERSION_RATE')}
          />
          <ASIAccordion
            expanded={true}
            lists={furtherInformation}
            title={t('FURTHER_INFORMATION')}
          />
          <ASIAccordion
            expanded={true}
            disc={false}
            lists={[t('ASI_MIGRATION_DISCLAIMER')]}
            title={t('DISCLAIMER')}
          />
        </>
      )}
    </div>
  );
};
